import React, {ReactNode, useEffect, useState} from 'react';
import classNames from 'classnames';
import type {LucideIcon} from 'lucide-react';
import {CircleAlertIcon, LoaderCircleIcon} from 'lucide-react';
import Icon from '../shared/Icon.tsx';

interface EmptyStateProps {
    icon: LucideIcon;
    // Says what belongs here.
    title: ReactNode;
    // One or two sentences on why it's useful.
    children?: ReactNode;
    // One primary action and at most one link.
    action?: ReactNode;
    link?: ReactNode;
    // The assets empty state is the drop zone, drawn with a dashed line.
    dashed?: boolean;
    className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
    icon,
    title,
    children,
    action,
    link,
    dashed,
    className
}) => (
    <div className={classNames('mx-empty', {'mx-empty-dashed': dashed}, className)}>
        <span className="mx-state-disc">
            <Icon icon={icon} size="l" />
        </span>
        <h3 className="mx-state-title">{title}</h3>
        {children && <p className="mx-state-text">{children}</p>}
        {action}
        {link}
    </div>
);

interface ErrorStateProps {
    // What happened: "Your projects didn't load"
    title: ReactNode;
    // Whether anything was lost, and what to do next.
    children?: ReactNode;
    onRetry?: () => void;
    retryLabel?: string;
    // A link instead of, or as well as, retry.
    action?: ReactNode;
    className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
    title,
    children,
    onRetry,
    retryLabel = 'Try again',
    action,
    className
}) => (
    <div role="alert" className={classNames('mx-error', className)}>
        <span className="mx-error-marker">
            <Icon icon={CircleAlertIcon} size={14} strokeWidth={3} />
        </span>
        <div className="mx-error-text">
            <b>{title}</b>
            {children && <span>{children}</span>}
            {(onRetry || action) && (
                <span className="mx-error-actions">
                    {onRetry && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={onRetry}
                        >
                            {retryLabel}
                        </button>
                    )}
                    {action}
                </span>
            )}
        </div>
    </div>
);

interface LoadingStateProps {
    // The shape of what's coming: a card grid, a list of rows or one block.
    variant?: 'cards' | 'rows' | 'block';
    count?: number;
    // Under 300ms nothing shows: a flash of skeleton feels slower than a wait.
    delay?: number;
    // Over two seconds, say what's happening.
    message?: ReactNode;
    className?: string;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
    variant = 'block',
    count = 4,
    delay = 300,
    message,
    className
}) => {
    const [visible, setVisible] = useState(delay === 0);

    useEffect(() => {
        if (delay === 0) {
            return;
        }
        const timer = window.setTimeout(() => setVisible(true), delay);
        return () => window.clearTimeout(timer);
    }, [delay]);

    if (!visible) {
        return <div className="mx-loading-pending" aria-busy="true" />;
    }

    return (
        <div
            className={classNames('mx-loading', `mx-loading-${variant}`, className)}
            aria-busy="true"
            role="status"
        >
            <span className="visually-hidden">Loading</span>
            {variant === 'block' ? (
                <div className="mx-skeleton mx-skeleton-block" />
            ) : (
                Array.from({length: count}, (_, i) => (
                    <div key={i} className="mx-skeleton-item">
                        {variant === 'cards' && (
                            <div className="mx-skeleton mx-skeleton-thumb" />
                        )}
                        <div className="mx-skeleton mx-skeleton-line" />
                        <div className="mx-skeleton mx-skeleton-line short" />
                    </div>
                ))
            )}
            {message && <div className="mx-loading-message">{message}</div>}
        </div>
    );
};

interface InlineProgressProps {
    // What's happening, in words: "Reading your whiteboard for feedback"
    title: ReactNode;
    // A rough time: "Usually about 20 seconds. You can keep working."
    detail?: ReactNode;
    onCancel?: () => void;
    className?: string;
}

// For anything over ten seconds: never blocks the rest of the page.
export const InlineProgress: React.FC<InlineProgressProps> = ({
    title,
    detail,
    onCancel,
    className
}) => (
    <div className={classNames('mx-progress', className)} role="status">
        <Icon icon={LoaderCircleIcon} size="s" className="mx-spin" />
        <span className="mx-progress-text">
            <b>{title}</b>
            {detail && <span>{detail}</span>}
        </span>
        {onCancel && (
            <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onCancel}
            >
                Cancel
            </button>
        )}
    </div>
);
